import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import ApiService from '../services/api';

const CompetitorWordHistory = ({ competitor, onBack }) => {
  const { user } = useAuth();
  const [words, setWords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [beeInfo, setBeeInfo] = useState(null);

  useEffect(() => {
    loadWordHistory();
  }, [competitor]);

  const loadWordHistory = async () => {
    if (!competitor || !user) return;

    try {
      setLoading(true);
      setError(null);

      // Use competitor.current_bee if available, otherwise backend will auto-detect
      const beeId = competitor.current_bee || null;

      const response = await ApiService.getCompetitorWordHistory(
        competitor.competitor_id,
        beeId,
        user.user_id
      );

      if (response.words) {
        setWords(response.words);
        setBeeInfo(response.bee_id);
      } else {
        setError('Failed to load word history');
      }
    } catch (err) {
      setError('Failed to load word history: ' + err.message);
      console.error('Error loading word history:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) {
      return 'Never';
    }
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const formatLast2Correct = (value) => {
    if (value === 'N/A') return 'N/A';
    return value ? 'Yes' : 'No';
  };

  const getLast2CorrectColor = (value) => {
    if (value === 'N/A') return '#666';
    return value ? '#28a745' : '#dc3545';
  };

  if (loading) {
    return (
      <div className="card">
        <div className="loading">Loading word history...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card">
        <div className="error">
          <h3>Error</h3>
          <p>{error}</p>
          <button
            onClick={loadWordHistory}
            className="button"
            style={{ marginTop: '10px' }}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="word-history-header">
        <button onClick={onBack} className="back-button">
          ← Back
        </button>
        <h2 className="card-title">All Practice Words</h2>
        <p className="card-subtitle">
          {competitor.name} • {beeInfo ? `Bee: ${beeInfo}` : 'Most recently practiced bee'}
        </p>
      </div>

      {words.length === 0 ? (
        <div className="empty-state">
          <p>No practice attempts yet</p>
          <p style={{ fontSize: '0.9rem', color: '#666' }}>
            Word history will appear here once {competitor.name} starts practicing.
          </p>
        </div>
      ) : (
        <div className="table-container">
          <p style={{ fontSize: '0.9rem', color: '#666', marginBottom: '15px', textAlign: 'center' }}>
            Showing {words.length} word{words.length !== 1 ? 's' : ''} (sorted by times shown)
          </p>
          <table className="word-history-table">
            <thead>
              <tr>
                <th style={{ textAlign: 'center', width: '60px' }}>#</th>
                <th style={{ textAlign: 'left' }}>Word</th>
                <th style={{ textAlign: 'center' }}>Times Shown</th>
                <th style={{ textAlign: 'center' }}>Last 2 Correct?</th>
                <th style={{ textAlign: 'right' }}>Date Last Shown</th>
              </tr>
            </thead>
            <tbody>
              {words.map((item, index) => {
                const isUnpracticed = item.times_shown === 0;
                return (
                  <tr key={index} style={isUnpracticed ? { opacity: 0.5 } : {}}>
                    <td style={{ textAlign: 'center', color: '#666', fontWeight: '600' }}>
                      {index + 1}
                    </td>
                    <td style={{ fontWeight: '600', fontSize: '1.1rem' }}>
                      {item.word}
                    </td>
                    <td style={{ textAlign: 'center', fontWeight: '600', color: isUnpracticed ? '#999' : 'inherit' }}>
                      {item.times_shown}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <span
                        style={{
                          color: getLast2CorrectColor(item.last_2_correct),
                          fontWeight: '600'
                        }}
                      >
                        {formatLast2Correct(item.last_2_correct)}
                      </span>
                    </td>
                    <td style={{ textAlign: 'right', color: isUnpracticed ? '#999' : '#666', fontSize: '0.9rem', fontStyle: isUnpracticed ? 'italic' : 'normal' }}>
                      {formatDate(item.date_last_shown)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <style jsx>{`
        .word-history-header {
          margin-bottom: 30px;
        }

        .back-button {
          background: none;
          border: none;
          color: #007bff;
          cursor: pointer;
          font-size: 1rem;
          padding: 5px 0;
          margin-bottom: 15px;
          transition: color 0.2s;
        }

        .back-button:hover {
          color: #0056b3;
        }

        .card-title {
          font-size: 1.8rem;
          font-weight: 700;
          color: #2d3748;
          margin-bottom: 0.5rem;
        }

        .card-subtitle {
          font-size: 1rem;
          color: #718096;
          margin-bottom: 1rem;
        }

        .table-container {
          overflow-x: auto;
        }

        .word-history-table {
          width: 100%;
          border-collapse: collapse;
          margin-top: 10px;
        }

        .word-history-table thead {
          background: #f8f9fa;
          border-bottom: 2px solid #e9ecef;
        }

        .word-history-table th {
          padding: 12px 15px;
          font-size: 0.85rem;
          font-weight: 600;
          color: #666;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .word-history-table td {
          padding: 15px;
          border-bottom: 1px solid #e9ecef;
        }

        .word-history-table tbody tr:hover {
          background: #f8f9fa;
        }

        .word-history-table tbody tr:last-child td {
          border-bottom: none;
        }

        .empty-state {
          text-align: center;
          padding: 40px 20px;
          color: #666;
        }

        .empty-state p:first-child {
          font-size: 1.2rem;
          color: #333;
          margin-bottom: 10px;
        }

        .loading {
          text-align: center;
          padding: 40px;
          color: #666;
        }

        .error {
          text-align: center;
          color: #dc3545;
        }

        @media (max-width: 768px) {
          .word-history-table {
            font-size: 0.9rem;
          }

          .word-history-table th,
          .word-history-table td {
            padding: 10px 8px;
          }

          .card-title {
            font-size: 1.5rem;
          }

          .word-history-table th:first-child,
          .word-history-table td:first-child {
            display: none;
          }
        }
      `}</style>
    </div>
  );
};

export default CompetitorWordHistory;
